import { apiClient } from './client';

export interface WorkoutSetLog {
  weight: string | null;
  reps: string | null;
}

export interface WorkoutExerciseLog {
  id: number;
  sortOrder: number;
  exerciseName: string;
  groupSortOrder: number | null;
  groupNote: string | null;
  plannedSets: number | null;
  plannedReps: number | null;
  plannedWeight: string | null;
  comment: string | null;
  setLogs: WorkoutSetLog[];
  updatedAt: string | null;
}

export interface WorkoutSession {
  id: number;
  blockId: number | null;
  blockName: string | null;
  programActivationId: number | null;
  currentLogId: number | null;
  startedAt: string;
  finishedAt: string | null;
  updatedAt: string | null;
  exercises: WorkoutExerciseLog[];
}

export interface WorkoutSessionSummary {
  id: number;
  blockId?: number | null;
  blockName: string | null;
  startedAt: string;
  finishedAt: string;
}

export interface WorkoutExerciseHistoryEntry {
  logId: number;
  sessionId: number;
  sessionStartedAt: string;
  blockName: string | null;
  exerciseName: string;
  plannedSets: number | null;
  plannedReps: number | null;
  plannedWeight: string | null;
  comment: string | null;
  setLogs: WorkoutSetLog[];
}

export interface WorkoutExerciseHistory {
  exerciseName: string;
  entries: WorkoutExerciseHistoryEntry[];
}

export interface CoachTodayLiveTrainee {
  traineeId: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  activeSession: WorkoutSession | null;
}

export interface CoachTodayLiveResponse {
  trainees: CoachTodayLiveTrainee[];
}

export const workoutApi = {
  async startSession(blockId: number, programActivationId?: number | null): Promise<WorkoutSession> {
    return apiClient.post<WorkoutSession>('/v1/workout/sessions', {
      blockId,
      programActivationId: programActivationId ?? null,
    });
  },

  async getActiveSession(): Promise<WorkoutSession | null> {
    try {
      const session = await apiClient.get<WorkoutSession | null>('/v1/workout/sessions/active');
      return session?.id != null ? session : null;
    } catch {
      return null;
    }
  },

  async getSession(sessionId: number): Promise<WorkoutSession> {
    return apiClient.get<WorkoutSession>(`/v1/workout/sessions/${sessionId}`);
  },

  async setCurrentExercise(
    sessionId: number,
    logId: number,
    expectedSessionUpdatedAt?: string | null,
  ): Promise<WorkoutSession> {
    return apiClient.put<WorkoutSession>(`/v1/workout/sessions/${sessionId}/current`, {
      logId,
      expectedSessionUpdatedAt: expectedSessionUpdatedAt ?? null,
    });
  },

  async updateExerciseLog(
    sessionId: number,
    logId: number,
    comment: string | null,
    setLogs: WorkoutSetLog[],
    expectedUpdatedAt?: string | null,
  ): Promise<WorkoutExerciseLog> {
    return apiClient.put<WorkoutExerciseLog>(
      `/v1/workout/sessions/${sessionId}/exercises/${logId}`,
      { comment, setLogs, expectedUpdatedAt: expectedUpdatedAt ?? null },
    );
  },

  async finishSession(sessionId: number): Promise<WorkoutSession> {
    return apiClient.post<WorkoutSession>(`/v1/workout/sessions/${sessionId}/finish`, {});
  },

  async cancelSession(sessionId: number): Promise<void> {
    await apiClient.delete(`/v1/workout/sessions/${sessionId}`);
  },

  async getExerciseHistory(name: string): Promise<WorkoutExerciseHistory> {
    const q = encodeURIComponent(name);
    return apiClient.get<WorkoutExerciseHistory>(`/v1/workout/exercises/history?name=${q}`);
  },

  async listHistory(limit?: number, offset?: number): Promise<WorkoutSessionSummary[]> {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    if (offset != null) params.set('offset', String(offset));
    const q = params.toString();
    return apiClient.get<WorkoutSessionSummary[]>(
      `/v1/workout/sessions/history${q ? `?${q}` : ''}`,
    );
  },

  async getCoachTodayLive(): Promise<CoachTodayLiveResponse> {
    return apiClient.get<CoachTodayLiveResponse>('/v1/workout/coach/today-live');
  },

  async coachStartSession(
    traineeId: number,
    blockId: number,
    programActivationId?: number | null,
    options?: { unplanned?: boolean },
  ): Promise<WorkoutSession> {
    return apiClient.post<WorkoutSession>('/v1/workout/coach/sessions', {
      traineeId,
      blockId,
      programActivationId: programActivationId ?? null,
      unplanned: options?.unplanned ?? false,
    });
  },

  async coachGetSession(sessionId: number): Promise<WorkoutSession> {
    return apiClient.get<WorkoutSession>(`/v1/workout/coach/sessions/${sessionId}`);
  },

  async coachSetCurrentExercise(
    sessionId: number,
    logId: number,
    expectedSessionUpdatedAt?: string | null,
  ): Promise<WorkoutSession> {
    return apiClient.put<WorkoutSession>(`/v1/workout/coach/sessions/${sessionId}/current`, {
      logId,
      expectedSessionUpdatedAt: expectedSessionUpdatedAt ?? null,
    });
  },

  async coachUpdateExerciseLog(
    sessionId: number,
    logId: number,
    comment: string | null,
    setLogs: WorkoutSetLog[],
    expectedUpdatedAt?: string | null,
  ): Promise<WorkoutExerciseLog> {
    return apiClient.put<WorkoutExerciseLog>(
      `/v1/workout/coach/sessions/${sessionId}/exercises/${logId}`,
      { comment, setLogs, expectedUpdatedAt: expectedUpdatedAt ?? null },
    );
  },

  async coachFinishSession(sessionId: number): Promise<WorkoutSession> {
    return apiClient.post<WorkoutSession>(`/v1/workout/coach/sessions/${sessionId}/finish`, {});
  },

  async coachCancelSession(sessionId: number): Promise<void> {
    await apiClient.delete(`/v1/workout/coach/sessions/${sessionId}`);
  },

  async coachGetExerciseHistory(traineeId: number, name: string): Promise<WorkoutExerciseHistory> {
    const q = encodeURIComponent(name);
    return apiClient.get<WorkoutExerciseHistory>(
      `/v1/workout/coach/trainees/${traineeId}/exercises/history?name=${q}`,
    );
  },

  async coachListHistory(
    traineeId: number,
    limit?: number,
    offset?: number,
  ): Promise<WorkoutSessionSummary[]> {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    if (offset != null) params.set('offset', String(offset));
    const q = params.toString();
    return apiClient.get<WorkoutSessionSummary[]>(
      `/v1/workout/coach/trainees/${traineeId}/sessions/history${q ? `?${q}` : ''}`,
    );
  },
};
